/**
 * Tripper-scoped Button override.
 *
 * Drop-in replacement for `@ttc3k/trekker` Button with v2 token styling.
 * Accepts the same core props as the Trekker Button so switching back is a
 * one-line import change once the design system catches up.
 *
 * v2 token mapping (see docs/trekker-v2-tokens.json):
 *
 *   Primary (filled):
 *     Success      → background/success/bold (#66800B), text/inverse (#FFFDF6)
 *     Information   → background/information/bold (#205EA6), text/inverse (#FFFDF6)
 *     Neutral       → background/base/inverted (#100F0F), text/inverse (#FFFDF6)
 *     Danger        → background/danger/bold (#AF3029), text/inverse (#FFFDF6)
 *
 *   Secondary (outlined):
 *     Success      → background/success/default (#EDEECF), border/success (#66800B), text/success (#536907)
 *     Information   → background/information/default (#E1ECEB), border/information (#205EA6), text/information (#205EA6)
 *     Neutral       → background/neutral/subtler (#E6E4D9), border/bold (#B7B5AC), text/default (#100F0F)
 *     Danger        → background/danger/default (#FFE1D5), border/danger (#AF3029), text/danger (#AF3029)
 *
 *   Sizes: sm=32px, md=40px, lg=48px
 *   Radius: radii-100 (8px)
 *   Font: body / medium (500)
 *   Disabled: opacity/disabled (0.4)
 */
import { forwardRef, type JSXElementConstructor } from "react";
import { css, cx } from "styled-system/css";
import { styled } from "styled-system/jsx";
import type { SystemStyleObject } from "styled-system/types";

/* ─── v1 prop types (must match @ttc3k/trekker Button) ────────────── */

type ButtonVisual =
  | "success"
  | "warning"
  | "highlight"
  | "primary"
  | "destructive"
  | "accent.blue"
  | "accent.purple"
  | "accent.magenta"
  | "unstyled";

type ButtonMode = "dark" | "light";
type ButtonSize = "sm" | "md" | "lg";

type ButtonSharedProps = {
  visual?: ButtonVisual;
  mode?: ButtonMode;
  size?: ButtonSize;
  isLoading?: boolean;
  Icon?: JSXElementConstructor<any>;
  isIconRightAligned?: boolean;
};

type ButtonAsButtonProps = {
  as?: "button";
} & React.ComponentPropsWithRef<"button"> &
  ButtonSharedProps &
  Omit<SystemStyleObject, keyof ButtonSharedProps>;

type ButtonAsLinkProps = {
  as: "a";
  disabled?: boolean;
} & React.ComponentPropsWithRef<"a"> &
  ButtonSharedProps &
  Omit<SystemStyleObject, keyof ButtonSharedProps>;

type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

/* ─── v1 visual → v2 "use" mapping ────────────────────────────────── */

type V2Use = "success" | "information" | "neutral" | "danger" | "warning";

function resolveUse(visual: ButtonVisual | undefined): V2Use | null {
  switch (visual) {
    case "success":
      return "success";
    case "accent.blue":
    case "highlight":
      return "information";
    case "primary":
      return "neutral";
    case "destructive":
      return "danger";
    case "warning":
      return "warning";
    case "unstyled":
    case "accent.purple":
    case "accent.magenta":
      return null;
    default:
      return "neutral";
  }
}

/* ─── Color tokens per v2 use × type ──────────────────────────────── */

// Primary (filled) – background/*/bold, text/inverse
const PRIMARY_COLORS: Record<
  V2Use,
  { bg: string; bgHover: string; text: string }
> = {
  // background/success/bold, background/success/bold/hovered, text/inverse
  success: { bg: "#66800B", bgHover: "#536907", text: "#FFFDF6" },
  // background/information/bold, background/information/bold/hovered, text/inverse
  information: { bg: "#205EA6", bgHover: "#1A4F8C", text: "#FFFDF6" },
  // background/base/inverted, background/base/inverted/hovered, text/inverse
  neutral: { bg: "#100F0F", bgHover: "#282726", text: "#FFFDF6" },
  // background/danger/bold, background/danger/bold/hovered, text/inverse
  danger: { bg: "#AF3029", bgHover: "#942822", text: "#FFFDF6" },
  // background/warning/bold, background/warning/bold/hovered, text/warning/inverse
  warning: { bg: "#AD8301", bgHover: "#8E6B01", text: "#100F0F" },
};

// Secondary (outlined) – background/*/default, border/*, text/*
const SECONDARY_COLORS: Record<
  V2Use,
  { bg: string; bgHover: string; border: string; text: string }
> = {
  // background/success/default, background/success/default/hovered, border/success, text/success
  success: {
    bg: "#EDEECF",
    bgHover: "#DDE2B2",
    border: "#66800B",
    text: "#536907",
  },
  // background/information/default, background/information/default/hovered, border/information, text/information
  information: {
    bg: "#E1ECEB",
    bgHover: "#C6DDE8",
    border: "#205EA6",
    text: "#205EA6",
  },
  // background/neutral/subtler, background/neutral/subtler/hovered, border/bold, text/default
  neutral: {
    bg: "#E6E4D9",
    bgHover: "#DAD8CE",
    border: "#B7B5AC",
    text: "#100F0F",
  },
  // background/danger/default, background/danger/default/hovered, border/danger, text/danger
  danger: {
    bg: "#FFE1D5",
    bgHover: "#FFCABB",
    border: "#AF3029",
    text: "#AF3029",
  },
  // background/warning/default, background/warning/default/hovered, border/warning, text/warning
  warning: {
    bg: "#FAEEC6",
    bgHover: "#F5E19B",
    border: "#AD8301",
    text: "#AD8301",
  },
};

/* ─── Size tokens ─────────────────────────────────────────────────── */

// spacing/150 = 12px, spacing/200 = 16px
// font/size/400 = 14px, font/size/500 = 16px
// font/height/500 = 22px, font/height/600 = 24px
const SIZE_STYLES: Record<
  ButtonSize,
  { height: string; px: string; py: string; fontSize: string; lineHeight: string }
> = {
  sm: { height: "32px", px: "12px", py: "12px", fontSize: "14px", lineHeight: "22px" },
  md: { height: "40px", px: "16px", py: "12px", fontSize: "14px", lineHeight: "22px" },
  lg: { height: "48px", px: "16px", py: "12px", fontSize: "16px", lineHeight: "24px" },
};

/* ─── Base styles ─────────────────────────────────────────────────── */

const buttonBase = css({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "100", // spacing/100 = 8px
  fontFamily: "body",
  fontWeight: 500, // font/weight/medium
  whiteSpace: "nowrap",
  userSelect: "none",
  cursor: "pointer",
  borderRadius: "8px", // radii-100
  border: "1px solid transparent",
  transition: "background-color 150ms ease, border-color 150ms ease",
  _disabled: {
    opacity: 0.4, // opacity/disabled
    cursor: "not-allowed",
  },
});

/* ─── Component ───────────────────────────────────────────────────── */

const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(function Button(props, ref) {
  const {
    as,
    visual,
    mode,
    size = "md",
    isLoading,
    Icon: IconComp,
    isIconRightAligned,
    children,
    className,
    disabled,
    // Pull out Panda style props that are commonly passed through
    w,
    width,
    alignSelf,
    flexShrink,
    ...rest
  } = props as any;

  const use = resolveUse(visual);
  const isPrimary = mode !== "light";
  const sizeTokens = SIZE_STYLES[size as ButtonSize] ?? SIZE_STYLES.md;

  // Build inline style for color tokens
  const colorStyle: React.CSSProperties = {};
  let hoverBg: string | undefined;

  if (use) {
    if (isPrimary) {
      const c = PRIMARY_COLORS[use];
      colorStyle.backgroundColor = c.bg;
      colorStyle.color = c.text;
      colorStyle.borderColor = c.bg;
      hoverBg = c.bgHover;
    } else {
      const c = SECONDARY_COLORS[use];
      colorStyle.backgroundColor = c.bg;
      colorStyle.color = c.text;
      colorStyle.borderColor = c.border;
      hoverBg = c.bgHover;
    }
  }

  // For unstyled: no background, border, or colors — just inherit
  const isUnstyled = visual === "unstyled";

  const icon = IconComp ? (
    <IconComp width={size === "sm" ? 14 : 16} height={size === "sm" ? 14 : 16} />
  ) : null;

  const content = (
    <>
      {icon && !isIconRightAligned && icon}
      {children}
      {icon && isIconRightAligned && icon}
    </>
  );

  const Tag = as === "a" ? styled.a : styled.button;

  return (
    <Tag
      ref={ref as any}
      className={cx(buttonBase, className)}
      disabled={as === "a" ? undefined : (disabled || isLoading)}
      aria-disabled={disabled || isLoading || undefined}
      // Panda CSS style props
      w={w ?? width}
      alignSelf={alignSelf}
      flexShrink={flexShrink}
      style={{
        ...(isUnstyled
          ? { background: "none", border: "none", color: "inherit" }
          : colorStyle),
        height: sizeTokens.height,
        paddingInline: sizeTokens.px,
        paddingBlock: sizeTokens.py,
        fontSize: sizeTokens.fontSize,
        lineHeight: sizeTokens.lineHeight,
        "--btn-hover-bg": hoverBg,
      }}
      onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
        if (!disabled && !isLoading && hoverBg) {
          (e.currentTarget as HTMLElement).style.backgroundColor = hoverBg;
        }
        (rest as any).onMouseEnter?.(e);
      }}
      onMouseLeave={(e: React.MouseEvent<HTMLElement>) => {
        if (!disabled && !isLoading && !isUnstyled && use) {
          const bg = isPrimary
            ? PRIMARY_COLORS[use].bg
            : SECONDARY_COLORS[use].bg;
          (e.currentTarget as HTMLElement).style.backgroundColor = bg;
        }
        (rest as any).onMouseLeave?.(e);
      }}
      {...rest}
    >
      {isLoading ? "…" : content}
    </Tag>
  );
});

export { Button };
export type { ButtonProps };
